// client/src/lib/analyticsRouteMap.ts
// Turn a mutating API call into a GA4 event so EVERY endpoint is auto-tracked
// (the "Tier-1 auto route->event map" from the event dictionary). Nothing drops
// silently: unmapped mutating routes still fire a generic entity_* event.
// Pure + testable.

/** Full request URL -> low-cardinality endpoint path (drop origin+/api, query, IDs). */
export function normalizeApiPath(url: string): string {
  let path = url;
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://x');
    path = u.pathname;
  } catch { /* keep raw string */ }
  const apiIdx = path.indexOf('/api/');
  if (apiIdx >= 0) path = path.slice(apiIdx + 4); // keep the leading '/...'
  return path
    .replace(/\/[a-f0-9]{24}(?=\/|$)/gi, '/:id') // Mongo ObjectIds
    .replace(/\/\d{3,}(?=\/|$)/g, '/:id')        // long numeric ids
    || '/';
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Non-CRUD action routes -> named events (checked before the CRUD fallback).
const EXPLICIT: Array<{ method: string; re: RegExp; event: string }> = [
  { method: 'POST', re: /^\/quotations\/generate/, event: 'quote_generation_started' },
  { method: 'POST', re: /^\/quotations\/price-lookup$/, event: 'price_lookup' },
  { method: 'POST', re: /^\/quotations\/:id\/send$/, event: 'quote_sent' },
  { method: 'PATCH', re: /^\/quotations\/:id\/status$/, event: 'quote_status_changed' },
  { method: 'POST', re: /^\/quotations\/:id\/revise$/, event: 'quote_revised' },
  { method: 'POST', re: /^\/quotations\/:id\/assign-client$/, event: 'quote_client_assigned' },
  { method: 'POST', re: /^\/rfqs\/manual/, event: 'rfq_uploaded' },
  { method: 'POST', re: /^\/rfqs\/:id\/reprocess$/, event: 'rfq_reprocessed' },
  { method: 'POST', re: /^\/rfq-split\/upload$/, event: 'rfq_uploaded' },
  { method: 'POST', re: /^\/inbox\/classify$/, event: 'email_classified' },
  { method: 'POST', re: /^\/inbox\/email\/:id\/classify$/, event: 'email_classified' },
  { method: 'POST', re: /^\/inbox\/email\/:id\/reply$/, event: 'email_reply_sent' },
  { method: 'POST', re: /^\/confirmed-orders\/manual$/, event: 'order_confirmed' },
  { method: 'POST', re: /^\/confirmed-orders\/:id\/post-po\/resolve$/, event: 'post_po_resolved' },
  { method: 'PATCH', re: /^\/confirmed-orders\/:id\/reconcile$/, event: 'order_reconciled' },
  { method: 'POST', re: /^\/vendor-purchase-orders\/from-confirmed/, event: 'vendor_po_created' },
  { method: 'POST', re: /^\/vendor-purchase-orders\/:id\/send$/, event: 'vendor_po_sent' },
  { method: 'POST', re: /^\/vendor-purchase-orders\/send-batch$/, event: 'vendor_po_sent' },
  { method: 'POST', re: /^\/vendor-rfq\/send$/, event: 'vendor_rfq_sent' },
  { method: 'POST', re: /^\/vendor-invoices\/upload/, event: 'vendor_invoice_uploaded' },
  { method: 'POST', re: /^\/vendor-invoices\/:id\/reconcile-vendor$/, event: 'vendor_invoice_reconciled' },
  { method: 'POST', re: /^\/client-invoices\/:id\/send$/, event: 'invoice_sent' },
  { method: 'POST', re: /^\/client-invoices\/from-confirmed/, event: 'invoice_created' },
  { method: 'POST', re: /^\/client-invoices\/manual$/, event: 'invoice_created' },
  { method: 'POST', re: /^\/credit-notes$/, event: 'credit_note_created' },
  { method: 'POST', re: /^\/client-price-lists\/:id\/send$/, event: 'client_pricelist_sent' },
  { method: 'PATCH', re: /^\/client-price-lists\/:id\/respond$/, event: 'client_pricelist_responded' },
  { method: 'POST', re: /^\/client-price-lists\/:id\/convert-to-confirmed-order$/, event: 'pricelist_converted_to_order' },
  { method: 'POST', re: /^\/master-catalogue\/import/, event: 'catalogue_imported' },
  { method: 'POST', re: /^\/marine\/match$/, event: 'marine_match_run' },
  { method: 'POST', re: /^\/marine\/confirm$/, event: 'marine_match_confirmed' },
];

// Resource segment -> entity name for the generic CRUD fallback.
const ENTITY: Record<string, string> = {
  vendors: 'vendor',
  clients: 'client',
  users: 'user',
  roles: 'role',
  currencies: 'currency',
  'currency-rates': 'currency_rate',
  templates: 'template',
  'master-catalogue': 'catalogue_item',
  'credit-notes': 'credit_note',
  'client-invoices': 'invoice',
  'vendor-invoices': 'vendor_invoice',
  'confirmed-orders': 'confirmed_order',
  'vendor-purchase-orders': 'vendor_po',
  quotations: 'quotation',
  rfqs: 'rfq',
  inventory: 'inventory_item',
};

const VERB: Record<string, string> = {
  POST: 'created',
  PUT: 'updated',
  PATCH: 'updated',
  DELETE: 'deleted',
};

/**
 * The GA4 event to fire for a SUCCESSFUL mutating call, or null (e.g. GETs).
 * @param endpoint a normalized path from normalizeApiPath (IDs -> :id).
 */
export function deriveApiEvent(
  method: string,
  endpoint: string
): { name: string; params: Record<string, any> } | null {
  const m = method.toUpperCase();
  if (!MUTATING.has(m)) return null;

  for (const r of EXPLICIT) {
    if (r.method === m && r.re.test(endpoint)) return { name: r.event, params: {} };
  }

  const seg = endpoint.replace(/^\/+/, '').split('/')[0] || '';
  const entity = ENTITY[seg] || seg.replace(/-/g, '_');
  return { name: `entity_${VERB[m] || 'changed'}`, params: { entity, path: endpoint } };
}
